import VectorTileLayer from 'ol/layer/VectorTile';
import LayerGroup from 'ol/layer/Group';
import ImageLayer from 'ol/layer/Image';
import Overlay from 'ol/Overlay';
import TileLayer from 'ol/layer/Tile';
import KmlFormat from 'ol/format/KML';
import ImageWMSSource from 'ol/source/ImageWMS';
import TileWMS from 'ol/source/TileWMS';
import Cluster from 'ol/source/Cluster';
import TileGrid from 'ol/tilegrid/TileGrid';
import { get } from 'ol/proj';
import { getWidth } from 'ol/extent';
import VectorTileWMSSource from './source/VectorTileWMS';
import TimeMixin from './mixin/TimeMixin';
import UnitMixin from './mixin/UnitMixin';
import { createImageLoader, createVectorTileLoader, createVectorSourceLoader } from './featureLoader';
import StyleFactory from './StyleFactory';
import { getViewPortBorders } from './utils/mapUtils';
import VectorLayerWMS from './layer/VectorLayerWMS';
import VectorSourceWMS from './source/VectorSourceWMS';
import { INFINITE_EXTENT, EXTENT } from './proj/epsg3395';
import * as defaultStyleElements from './styles/elements/index';
import { updateURLWithParameters, isLayerAbleToChangeUnit } from './utils/unitConversion';
import ExtremeMixin from './mixin/ExtremeMixin';
const TimeVectorTileWMSSource = ExtremeMixin(UnitMixin(TimeMixin(VectorTileWMSSource)));
const TimeVectorWMSSource = ExtremeMixin(TimeMixin(VectorSourceWMS));
/**
 * @typedef {Object} LayersFactoryOptions
 * @property {string} chartWMSUrl Chart Server WMS url
 * @property {string} weatherWMSUrl Weather WMS url
 * @property {string} weatherBorderWMSUrl Weather border WMS url
 * @property {string} weatherNonGridUrl Weather nongrid WMS url
 * @property {string} portsUrl ports url
 * @property {string} legsUrl legs url
 * @property {EventTarget} player Instance of Player
 * @property {object} [themeConfig] Weather elements theme config
 * @property {object} [styleElements] Styles for elements
 * @property {function(string, object):Promise} [featureLoader = fetch] Custom loader.
 * Optional function to load a tile/feature given a URL. Could look like this:
 * @example
 *
 *  async function authLoader(url) {
 *    const token = await getTokenAsync();
 *    return fetch(url, {
 *      headers: {
 *       Authorization: `Bearer ${token}`,
 *      },
 *    });
 *  };
 */

/**
 * @typedef {Object} WeatherParams
 * @property {object} element Weather element.
 * @property {object} view Weather view mode.
 * @property {object} forecast Weather forecast version.
 */

/**
 * @typedef {Object} WeatherLayerOptions
 * @property {WeatherParams} params Weather request params.
 * At least a `view`, `element` and `forecast` params are required.
 * `version` is `v1` by default.
 * `format` is `kml` by default.
 * `x`, `y`, `z` will be set dynamically.
 * @property {number} [timeBucketMs=3600000] Time bucket in mileseconds.
 * @property {string} [title] Layer title.
 * @property {number} [opacity=1] Layer opacity.
 * @property {boolean} [visible=true] Weather the layer visible.
 * @property {string} [projection] Projection.
 * @property {import('ol/extent').Extent} [extent] Extent.
 * @property {Number} [maxZoom=9] Optional max zoom level.
 * @property {number} [minZoom=0] Optional min zoom level.
 * @property {number} [preloadZoom=0] Load low resolution tiles up to 'preload' zoom levels.
 * @property {number} [cacheSize] Cache size
 * @property {import('ol/style/Style').default} [style] Optional custom style.
 * @property {object} [viewOptions] Style options.
 */

/**
 * @typedef {Object} WeatherNonGridParams
 * @property {object} element Weather element.
 * @property {object} forecast Weather forecast version.
 * @property {number} [past_track_period] Optional weather past track period.
 */

/**
 * @typedef {Object} WeatherNonGridLayerOptions
 * @property {WeatherNonGridParams} params Weather request params.
 * At least a `view`, `element` and `forecast` params are required.
 * `version` is `v1` by default.
 * `format` is `kml` by default.
 * `x`, `y`, `z` will be set dynamically.
 * @property {number} [timeBucketMs=3600000] Time bucket in mileseconds.
 * @property {string} [title] Layer title.
 * @property {boolean} [visible=true] Weather the layer visible.
 * @property {string} [projection] Projection.
 * @property {import('ol/style/Style').default} [style] Optional custom style.
 * @property {object} [viewOptions] Style options.
 */

/**
 * @classdesc
 * A LayersFactory provide ability to create OpenLayers Layer instances
 * for different kinds of weather elements and views.
 *
 * @example
 * async function authLoader(url) {
  *   const accessToken = await AuthService.getAccessToken(env.scope);
  *   return fetch(url, {
  *     headers: {
  *       Authorization: `Bearer ${accessToken}`,
  *     },
  *   });
  * }
 *
 * const layersFactory = new LayersFactory({
  *   chartWMSUrl: config.chartWMSUrl,
  *   weatherWMSUrl: config.weatherWMSUrl,
  *   player: new Player(),
  *   featureLoader: authLoader,
  * });
 */

class LayersFactory {
  /**
   * @param {!LayersFactoryOptions} options
   */
  constructor({
    chartWMSUrl,
    weatherWMSUrl,
    weatherBorderWMSUrl,
    weatherNonGridUrl,
    portsUrl,
    legsUrl,
    player,
    featureLoader,
    themeConfig
  }) {
    this.weatherWMSUrl = weatherWMSUrl;
    this.weatherBorderWMSUrl = weatherBorderWMSUrl;
    this.weatherNonGridUrl = weatherNonGridUrl;
    this.portsUrl = portsUrl;
    this.legsUrl = legsUrl;
    this.chartWMSUrl = chartWMSUrl;
    this.player = player;
    this.featureLoader = featureLoader || fetch;
    this.styles = new StyleFactory(defaultStyleElements, themeConfig);
  }
  /**
   * Creates CharServer ImageLayer
   *
   * @param {object} options
   * @param {object} options.params
   * @returns {import('ol/layer/Image').default}
   */


  createCartImageWMSLayer({
    params,
    ...layerOptions
  }) {
    return new ImageLayer({ ...layerOptions,
      extent: INFINITE_EXTENT,
      source: new ImageWMSSource({
        url: this.chartWMSUrl,
        ratio: 1,
        imageLoadFunction: createImageLoader(this.featureLoader),
        params: { ...params
        }
      })
    });
  }
  /**
   * Creates CharServer TileLayer
   *
   * @param {object} options
   * @param {object} options.params
   * @returns {import('ol/layer/Tile').default}
   */


  createChartWMSLayer({
    tileSize,
    params,
    ...layerOptions
  }) {
    const projExtent = get('EPSG:3395').getExtent();
    const startResolution = getWidth(projExtent) / 1024;
    const resolutions = new Array(16);

    for (let i = 0, ii = resolutions.length; i < ii; i += 1) {
      resolutions[i] = startResolution / 2 ** i;
    }

    return new TileLayer({ ...layerOptions,
      preload: Infinity,
      source: new TileWMS({
        url: this.chartWMSUrl,
        tileLoadFunction: createImageLoader(this.featureLoader),
        tileGrid: new TileGrid({
          resolutions,
          tileSize: tileSize || [1024, 1024],
          extent: EXTENT
        }),
        params: { ...params
        }
      })
    });
  }
  /**
   * Creates instans of weather WMS layer.
   *
   * @param {!WeatherNonGridLayerOptions} options Weather layer options
   * @returns {import("ol/layer/Vector").default} Vector tile layer
   */


  createWeatherVectorLayer({
    projection,
    style,
    params,
    viewOptions,
    timeBucketMs = 1000 * 60 * 60 * 1,
    // 1h
    ...layerOptions
  }) {
    const layer = new VectorLayerWMS({ ...layerOptions,
      renderBuffer: window.screen.width,
      style: style || this.styles.getStyle({
        element: params.element,
        options: viewOptions,
        additionalOptions: {
          unit: 'mps',
          plot_extrema: false
        }
      })
    });
    const source = new TimeVectorWMSSource({
      player: this.player,
      timeBucketMs,
      projection,
      url: updateURLWithParameters(this.weatherNonGridUrl, params.element, params),
      isLayerVisibleFn: () => layer.getVisible(),
      loader: createVectorSourceLoader(this.featureLoader),
      format: new KmlFormat({
        extractStyles: false
      }),
      params: {
        format: 'kml',
        version: 'v1',
        ...params
      }
    });
    this.player.addEventListener('unitupdate', e => {
      if (!isLayerAbleToChangeUnit(params.element)) {
        return;
      }

      layer.getSource().params.unit = e.detail.unit;
      layer.getSource().refresh();
    });
    layer.setSource(source);
    return layer;
  }
  /**
   * Creates instance of weather ports layer.
   *
   * @param {!WeatherLayerOptions} options Weather layer options
   */


  createPortsLayer({
    map,
    popupContainer,
    refreshOnMove,
    projection,
    style,
    params,
    ...layerOptions
  }) {
    if (refreshOnMove) {
      const viewPortBorders = getViewPortBorders(map);
      Object.assign(params, viewPortBorders);
    }

    let prevZoom = map.getView().getZoom();
    const portsLayer = new VectorLayerWMS({ ...layerOptions,
      renderBuffer: window.screen.width,
      style: style || this.styles.getStyle({
        element: params.element
      }),
      // @ts-ignore
      source: new Cluster({
        distance: layerOptions.clusterDistance || 40,
        source: new VectorSourceWMS({
          projection,
          url: this.portsUrl,
          loader: createVectorSourceLoader(this.featureLoader),
          format: new KmlFormat({
            extractStyles: false
          }),
          params: {
            format: 'kml',
            version: 'v1',
            left: params.left || -180,
            // westLongitude
            bottom: params.bottom || -90,
            // southLatitude
            right: params.right || 180,
            // eastLongitude
            top: params.top || 90,
            // northLatitude
            ...params
          }
        })
      })
    });
    const overlay = new Overlay({
      element: popupContainer,
      autoPan: true,
      autoPanAnimation: {
        duration: 250
      }
    });
    const element = overlay.getElement();
    element.style.display = 'block';
    map.addOverlay(overlay);
    portsLayer.on('change:visible', () => {
      overlay.setPosition(undefined);
    });
    map.on('click', evt => {
      if (!portsLayer.getVisible()) {
        return;
      }

      const {
        dragging,
        coordinate,
        pixel
      } = evt;
      overlay.setPosition(undefined);

      if (dragging) {
        return;
      }

      map.forEachFeatureAtPixel(pixel, (clusteredFeatures, layer) => {
        if (portsLayer !== layer) {
          return;
        }

        const features = clusteredFeatures.get('features');

        if (!features) {
          return;
        }

        const container = element.firstElementChild;
        let content = '';

        if (features.length === 1) {
          const props = features[0].getProperties();
          const propsToShow = [];
          Object.keys(props).forEach(k => {
            if (typeof props[k] === 'string') {
              propsToShow.push(`${k}: ${props[k]}`);
            }
          });
          content = `<div>${propsToShow.join('<br />')}</div>`;
        } else {
          content = `<div>Move closer.<br />Items: ${features.length}</div>`;
        }

        container.innerHTML = content;
        overlay.setPosition(coordinate);
      });
    });
    map.on('moveend', () => {
      if (!portsLayer.getVisible()) {
        return;
      }

      if (map.getView().getZoom() !== prevZoom) {
        prevZoom = map.getView().getZoom();
        overlay.setPosition(undefined);
      }

      if (refreshOnMove) {
        overlay.setPosition(undefined);
        const viewPortBorders = getViewPortBorders(map);
        const clusterSource = portsLayer.getSource();
        const portsSource = clusterSource.getSource();
        Object.assign(portsSource.params, viewPortBorders);
        portsSource.refresh();
      }
    });
    return portsLayer;
  }
  /**
   * Creates instance of weather legs layer.
   *
   * @param {!WeatherLayerOptions} options Weather layer options
   * @returns {import("ol/layer/Vector").default} Vector tile layer
   */


  createLegsLayer({
    map,
    maxZoom = 16,
    minZoom = 7,
    projection,
    style,
    params,
    ...layerOptions
  }) {
    const legsZoomInRange = zoom => zoom > minZoom && zoom <= maxZoom;

    const fitLegsZoomToRange = zoom => {
      if (legsZoomInRange(zoom)) {
        return zoom;
      }

      if (zoom < minZoom) {
        return minZoom + 1;
      }

      if (zoom > maxZoom) {
        return maxZoom;
      }

      return null;
    };

    Object.assign(params, getViewPortBorders(map));
    const legsLayer = new VectorLayerWMS({ ...layerOptions,
      maxZoom,
      minZoom,
      updateWhileAnimating: false,
      updateWhileInteracting: false,
      renderBuffer: window.screen.width,
      style: style || this.styles.getStyle({
        element: params.element
      }),
      // @ts-ignore
      source: new VectorSourceWMS({
        projection,
        url: this.legsUrl,
        loader: createVectorSourceLoader(this.featureLoader),
        format: new KmlFormat({
          extractStyles: false
        }),
        params: {
          format: 'kml',
          version: 'v1',
          zoomLevel: fitLegsZoomToRange(map.getView().getZoom()),
          ...params
        }
      })
    });
    map.on('moveend', () => {
      const newZoom = map.getView().getZoom();

      if (!legsLayer.getVisible() || !legsZoomInRange(newZoom)) {
        return;
      }

      const legsSource = legsLayer.getSource();
      const legParams = legsSource.params;
      legParams.zoomLevel = newZoom;
      Object.assign(legParams, getViewPortBorders(map));
      legsSource.updateParams(legParams);
      legsSource.refresh();
    });
    return legsLayer;
  }
  /**
   * Creates instance of weather border WMS layer.
   *
   * @param {!WeatherLayerOptions} options Weather layer options
   * @returns {import("ol/layer/Vector").default} Vector tile layer
   */


  createWeatherBorderWMSLayer({
    projection,
    style,
    params,
    ...layerOptions
  }) {
    return new VectorLayerWMS({ ...layerOptions,
      renderBuffer: window.screen.width,
      style: style || this.styles.getStyle({
        element: params.element,
        view: params.view
      }),
      // @ts-ignore
      source: new VectorSourceWMS({
        projection,
        url: this.weatherBorderWMSUrl,
        loader: createVectorSourceLoader(this.featureLoader),
        format: new KmlFormat({
          extractStyles: false
        }),
        params: {
          format: 'kml',
          version: 'v1',
          ...params
        }
      })
    });
  }
  /**
   * Creates instans of weather WMS layer.
   *
   * @param {!WeatherLayerOptions} options Weather layer options
   * @returns {import("ol/layer/VectorTile").default}
   */


  createWeatherWMSLayer({
    maxZoom = 9,
    minZoom,
    params,
    preloadZoom,
    cacheSize,
    projection,
    extent,
    style,
    timeBucketMs = 1000 * 60 * 60 * 1,
    // 1h,
    viewOptions,
    ...layerOptions
  }) {
    const stylesElement = params.additionalOptions && params.additionalOptions.customStylesElement ? params.additionalOptions.customStylesElement : params.element;
    const layer = new VectorTileLayer({ ...layerOptions,
      className: stylesElement,
      preload: preloadZoom,
      declutter: true,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
      renderBuffer: window.screen.width,
      extent,
      style: style || this.styles.getStyle({
        element: stylesElement,
        view: params.view,
        options: viewOptions,
        additionalOptions: Object.assign({}, {
          unit: 'mps',
          plot_extrema: false
        }, params.additionalOptions)
      })
    });
    const source = new TimeVectorTileWMSSource({
      wrapX: true,
      player: this.player,
      timeBucketMs,
      preloadZoom,
      cacheSize,
      projection,
      extent,
      maxZoom,
      minZoom,
      isLayerVisibleFn: () => layer.getVisible(),
      url: updateURLWithParameters(this.weatherWMSUrl, stylesElement, params),
      tileLoadFunction: createVectorTileLoader(this.featureLoader),
      format: new KmlFormat({
        extractStyles: false
      }),
      params: {
        format: 'kml',
        version: 'v1',
        layer,
        ...params
      }
    });
    layer.setSource(source);
    layer.on('change:visible', e => {
      e.target.getSource().refresh();
    });
    this.player.addEventListener('unitupdate', e => {
      if (!isLayerAbleToChangeUnit(stylesElement)) {
        return;
      }

      const newUnit = e.detail.unit === 'KTS' ? 'Kts' : '';
      const styleName = `${stylesElement}${newUnit}`;
      const newStyle = this.styles.getStyle({
        element: styleName,
        view: params.view,
        options: viewOptions,
        additionalOptions: Object.assign({}, params.additionalOptions, {
          unit: newUnit
        })
      });
      layer.setStyle(newStyle);
    });
    return layer;
  }

  setTheme(theme) {
    this.styles.setTheme(theme);
  }
  /**
   *
   * @param {object} layer
   * @param {function|import('ol/style/Style').default} [style]
   */


  updateStyle(layerObject, style) {
    if (layerObject instanceof LayerGroup) {
      const layers = layerObject.getLayersArray();
      layers.forEach(layer => {
        this.setLayerStyle(layer, style);
      });
    } else {
      this.setLayerStyle(layerObject, style);
    }
  }
  /**
   *
   * @param {object} layer
   * @param {function|import('ol/style/Style').default} [style]
   */


  setLayerStyle(layer, style) {
    if (typeof layer.getSource !== 'function') {
      return;
    }

    let source = layer.getSource();

    if (source instanceof Cluster) {
      source = source.getSource();
    } // @ts-ignore


    const {
      params
    } = source;

    if (params && layer && typeof layer.setStyle === 'function') {
      const newStyle = style || this.styles.getStyle(params);

      if (newStyle) {
        layer.setStyle(newStyle);
      }
    }
  }

}

export default LayersFactory;